import { Platform, StyleSheet } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';

const sans = { fontFamily: ticketFonts.sans } as const;

/** Shared styles for shell settings screens (Profile, BranchSettings, CashManagement, Billing). */
export const settingsStyles = StyleSheet.create({
  container: {
    ...ticketSurfaces.screen,
  },
  loadingWrap: {
    ...ticketSurfaces.screen,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.xxl,
    paddingHorizontal: spacing.lg,
  },
  title: {
    ...typography.titleXL,
    ...sans,
    color: ticketColors.ink,
    marginBottom: spacing.xs,
  },
  subtitle: {
    ...typography.body,
    ...sans,
    color: ticketColors.ink2,
    marginBottom: spacing.sm,
    lineHeight: 20,
  },
  card: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.lg,
    gap: spacing.md,
  },
  navListCard: {
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  cardTitle: {
    ...typography.titleLg,
    ...sans,
    color: ticketColors.ink,
    marginBottom: spacing.xs,
    flexShrink: 1,
  },
  sectionHeader: {
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  sectionTitle: {
    ...typography.titleLg,
    ...sans,
    color: ticketColors.ink,
  },
  sectionSubtitle: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 18,
    fontWeight: '600',
  },
  caption: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  captionBlock: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 20,
    marginBottom: spacing.xs,
  },
  captionStrong: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  inputsBlock: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  input: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  primaryButton: {
    backgroundColor: ticketColors.gold,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  primaryButtonText: {
    ...typography.body,
    ...sans,
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
  secondaryButton: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  secondaryButtonText: {
    ...typography.body,
    ...sans,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  disabled: {
    opacity: 0.6,
  },
  wideColumn: {
    flex: 1,
    minWidth: 280,
    maxWidth: '100%',
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  linkText: {
    ...typography.body,
    ...sans,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  screenLead: {
    ...typography.body,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  screenHeroTitle: {
    ...typography.titleLg,
    ...sans,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  secondaryCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    alignSelf: 'flex-start',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 40,
  },
  secondaryCtaText: {
    ...typography.body,
    ...sans,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  formFieldLabel: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink3,
    fontWeight: '800',
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.md,
    minHeight: 52,
  },
  navRowDisabled: {
    opacity: 0.55,
  },
  navRowBody: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  navRowTitle: {
    ...typography.body,
    ...sans,
    fontWeight: '700',
  },
  navRowDescription: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  navRowSeparator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: ticketColors.ruleSoft,
  },
  emptyHint: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 18,
    textAlign: 'center',
    padding: spacing.lg,
  },
  mainValue: {
    ...typography.titleXL,
    ...sans,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  /** Correo en card Cuenta: legible en anchos chicos (workspace, móvil). */
  accountEmail: {
    ...typography.body,
    ...sans,
    fontWeight: '700',
    color: ticketColors.ink,
    lineHeight: 22,
    flexShrink: 1,
    alignSelf: 'stretch',
    ...(Platform.OS === 'web'
      ? ({ overflowWrap: 'anywhere', wordBreak: 'break-word', maxWidth: '100%' } as const)
      : null),
  },
  identityCard: {
    minWidth: 0,
    flexShrink: 1,
    alignSelf: 'stretch',
  },
  cardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    backgroundColor: ticketColors.paper,
  },
  editButtonText: {
    ...typography.caption,
    ...sans,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  formHint: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 17,
  },
  warningText: {
    ...typography.caption,
    ...sans,
    color: ticketColors.gold,
    lineHeight: 18,
  },
  bulletLine: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  branchUsageLine: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink,
    fontWeight: '600',
    lineHeight: 20,
  },
  branchUsageWarning: {
    color: ticketColors.gold,
  },
  adminGroupLabel: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  adminGroupWrap: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
  },
  profilePairCard: {
    flex: 1,
    minWidth: 0,
    flexBasis: 280,
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
  retryBlock: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  retryButton: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  retryButtonDisabled: {
    opacity: 0.6,
  },
  retryButtonText: {
    ...typography.caption,
    ...sans,
    fontWeight: '700',
    color: ticketColors.gold,
  },
  listSectionTitle: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink3,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
  },
  auditRowWrap: {
    marginBottom: spacing.sm,
  },
  moreLinkBtn: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  moreLinkText: {
    ...typography.body,
    ...sans,
    color: ticketColors.gold,
    fontWeight: '600',
  },
  planRowCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  /** Banners tipo Billing — tokens oscuros reutilizables. */
  banner: {
    borderRadius: 14,
    borderWidth: 1,
    padding: spacing.md,
    gap: spacing.xs,
  },
  bannerAccent: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  bannerSuccess: {
    borderColor: 'rgba(74, 222, 128, 0.35)',
    backgroundColor: 'rgba(74, 222, 128, 0.08)',
  },
  bannerInfo: {
    borderColor: 'rgba(96, 165, 250, 0.35)',
    backgroundColor: 'rgba(96, 165, 250, 0.08)',
  },
  bannerTitle: {
    ...typography.body,
    ...sans,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  bannerBody: {
    ...typography.caption,
    ...sans,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  layoutWideRow: {
    flexDirection: 'row',
    gap: spacing.md,
    alignItems: 'flex-start',
  },
  layoutWideCol: {
    flex: 1,
    minWidth: 0,
  },
});
